import { useUser } from "@/contexts/user-provider";
import { TaskDefaultFilter } from "@/types/task";
import { useTaskSettings } from "@/api/provider/tasks/task-settings-query";
import { useTasksDrawerStore } from "@/stores/tasks-drawer-store";
export type TaskScope = "subscribed" | "assigned" | "all";

/**
 * Whose tasks the user is currently looking at.
 *
 * Three inputs, in precedence order: a clinic that has locked the filter wins
 * outright; otherwise the panel's own scope picker; otherwise the user's saved
 * preference.
 *
 * Shared rather than derived per screen, because the nav badge and the panel
 * count the same tasks. When only the panel knew about the override, changing
 * the scope moved the list and left the badge on the default — the same number
 * next to "Tasks" no matter what you picked.
 */
export function useEffectiveTaskScope() {
  const {
    currentUser
  } = useUser();
  const {
    data: taskSettings
  } = useTaskSettings();
  const assigneeFilter = useTasksDrawerStore(_temp);
  const clinicFilter = taskSettings?.defaultTaskFilter ?? TaskDefaultFilter.USER_CHOICE;
  if (clinicFilter !== TaskDefaultFilter.USER_CHOICE) {
    return clinicFilter === TaskDefaultFilter.ASSIGNED_TO_ME ? "assigned" : "subscribed";
  }
  const userPreferred = currentUser?.taskDefaultFilter === "ASSIGNED_TO_ME" ? "assigned" : "subscribed";
  return assigneeFilter ?? userPreferred;
}

/**
 * The scope as `/task/count` takes it on the wire.
 *
 * `allTasks` is not redundant with sending neither id: the count endpoint fills
 * an absent scope in from the caller's default, so "everything" has to be asked
 * for explicitly or it comes back as the default scope's number.
 */
function _temp(state) {
  return state.panelFilters.assigneeFilter;
}
export function taskScopeParams(scope: TaskScope, providerId: string | undefined): {
  assigneeId?: string;
  subscriberId?: string;
  allTasks?: boolean;
} {
  return {
    assigneeId: scope === "assigned" ? providerId : undefined,
    subscriberId: scope === "subscribed" ? providerId : undefined,
    allTasks: scope === "all" ? true : undefined
  };
}